import { StepAccordion, AccordionMinimumHeight, ButtonColorEnum, AccordionBackgroundColor } from "../../../../Utils/StepAccordion";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import { useContext, useMemo, useState } from "react";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import {
	Box,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from "@mui/material";
import { InputComponent } from "../../../../Utils/UI/InputComponent/InputComponent";
import { Colors } from "../../../../../Themes/colors";
import { useOrder } from "../../../../../Contexts/OrderContext";

export const BetalingsserviceStep = ({
	nextStep,
	previousStep,
	open,
	toggleOpen,
}: IStepInterface) => {
	const { order, updateOrder } = useOrder();
	const [regNumberError, setRegNumberError] = useState<boolean>(false);
	const [accountNumberError, setAccountNumberError] = useState<boolean>(false);
	const [subscribeToBs, setSubscribeToBs] = useState<boolean>(true);
	const partnerContentContext = useContext(PartnerContentContext);

	function regNumberValidator(input: string): boolean {
		return /^[0-9]{4}$/.test(input);
	}

	function accountNumberValidator(input: string): boolean {
		return /^[0-9]{1,10}$/.test(input);
	}

	function handleRegNumberChange(result: { value: string; valid: boolean; error: string }) {
		setRegNumberError(!result.valid && result.value.length > 0);
		updateOrder({ regNumber: result.value });
	}

	function handleAccountNumberChange(result: { value: string; valid: boolean; error: string }) {
		setAccountNumberError(!result.valid && result.value.length > 0);
		updateOrder({ accountNumber: result.value });
	}

	const formValid = useMemo(() => {
		if (subscribeToBs) {
			if (regNumberError || accountNumberError) {
				return false;
			}
			if (order.regNumber.length === 0 || order.accountNumber.length === 0) {
				return false;
			}
		}
		return true;
	}, [order.regNumber, order.accountNumber, accountNumberError, regNumberError, subscribeToBs]);

	return (
		<StepAccordion
			loading={false}
			loadingText={partnerContentContext.stepLoader.text}
			loadingTitle={partnerContentContext.stepLoader.title}
			label={partnerContentContext.salesInformationStep?.title || "Tilmeld Betalingsservice"}
			open={open}
			click={toggleOpen}
			firstButtonLabel={partnerContentContext.salesInformationStep?.backButton || "Tilbage"}
			firstButtonOutline={true}
			firstButton={previousStep}
			secondButtonLabel={partnerContentContext.salesInformationStep?.nextButton || "Næste"}
			secondButtonType={ButtonColorEnum.DARK_GREEN}
			secondButton={nextStep}
			secondButtonDisabled={!formValid}
			minimumHeight={AccordionMinimumHeight.SMALL}
			firstButtonId="bsStepReturn"
			secondButtonId="bsStepForward"
			secondButtonShadow={true}
			backgroundColor={AccordionBackgroundColor.GREY}
		>
			<Box display={"flex"} flexDirection={"column"} px={1.5}>
				<Typography mt={"10px"} mb={"15px"}>
					Bemærk venligst, at du skal angive en bankkonto, som ejes af personen med det tidligere oplyste CPR-nummer.
				</Typography>
				
				{!partnerContentContext.bsRequired && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
						<Box>Jeg vil gerne tilmelde mig Betalingsservice</Box>

						<FormControl component="fieldset" sx={{ marginRight: 2 }}>
							<RadioGroup
								row
								value={subscribeToBs ? "true" : "false"}
								onChange={(e) => {
									const subscribe = e.target.value === "true";
									setSubscribeToBs(subscribe);
									if (!subscribe) {
										setRegNumberError(false);
										setAccountNumberError(false);
										updateOrder({ regNumber: "", accountNumber: "" });
									}
								}}
							>
								<FormControlLabel value="true" control={<Radio sx={{ color: Colors.grey30, "&.Mui-checked": { color: Colors.greenStrong } }} />} label="Ja" />
								
								<FormControlLabel value="false" control={<Radio sx={{ color: Colors.grey30, "&.Mui-checked": { color: Colors.greenStrong } }} />} label="Nej" />
							</RadioGroup>
						</FormControl>
					</Box>
				)}

				<Box display={"flex"} flex={1}>
					<Box flex={1}>
						<InputComponent
							validationType="number"
							value={order.regNumber}
							placeholder="Reg. Nummer"
							change={handleRegNumberChange}
							customValidator={regNumberValidator}
							customTextValidationError="Ugyldigt reg. nummer"
							inputProps={{ maxLength: 4, autoComplete: "off" }}
							disabled={!subscribeToBs}
						/>
					</Box>

					<Box flex={1} ml={"10px"}>
						<InputComponent
							validationType="number"
							value={order.accountNumber}
							placeholder="Kontonummer"
							change={handleAccountNumberChange}
							customValidator={accountNumberValidator}
							customTextValidationError="Ugyldigt kontonummer"
							inputProps={{ maxLength: 10, autoComplete: "off" }}
							disabled={!subscribeToBs}
						/>
					</Box>
				</Box>
			</Box>
		</StepAccordion>
	);
};
